<template>
  <div id="Stats">
    <Toolbar :title="t('Reports')">
      <div slot="buttons">
        <v-btn icon @click="copy" :disabled="!executed">
          <v-icon>file_copy</v-icon>
        </v-btn>
      </div>
    </Toolbar>

    <v-expansion-panel v-model="active">
      <v-expansion-panel-content>
        <div slot="header" class="ml-4">{{ t('Parameters') }}</div>
        <v-card>
          <div v-if="showReportMultiSelect" id="report-cell-select">
            <div class="report-cell-select-label">{{ t('Cells') }}:</div>
            <v-checkbox
              v-for="cell in reportCellItems"
              :key="cell.value"
              :label="cell.text"
              :input-value="isReportCellSelected(cell.value)"
              hide-details
              class="report-cell-checkbox"
              @change="setReportCell(cell.value, $event)"
            />
          </div>
          <CellSelect v-else />

          <v-card-text id="cell-type" v-if="types.length > 1" class="py-0">
            <div>{{ t('Cell type') }}:</div>
            <v-radio-group v-model="cellTypeName" row class="mt-0">
              <v-radio v-for="p in types" :key="p" :label="t(p)" :value="p" />
            </v-radio-group>

            <!-- <v-checkbox v-for="(name, i) in types" :key="i" :label="t(name)"
              :inputValue="checkedTypes[i]" @change="toggleType(i)"/> -->
          </v-card-text>

          <v-card-text class="py-0 ml-2">
            <v-radio-group v-model="period" row class="mt-0" style="justify-content: flex-start">
              <v-radio v-for="p in periods" :key="p" :label="t(p)" :value="p" />
            </v-radio-group>

            <v-radio-group v-model="startingPoint" row class="mt-0" style="justify-content: flex-start">
              <v-radio v-for="p in startingPoints" :key="p" :label="t(p)" :value="p" />
            </v-radio-group>
          </v-card-text>
          <v-spacer />

          <v-card-actions class="pl-4">
            <v-btn @click="run" :disabled="runDisabled">{{ t('Perform') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content v-show="executed">
        <div slot="header" class="ml-4">{{ t('Report') }}</div>
        <v-tabs v-model="activeTab">
          <v-tab>{{ t('Persons') }}</v-tab>
          <v-tab>{{ t('Cells') }}</v-tab>
          <v-tab v-if="showSections">{{ t('Sections') }}</v-tab>

          <!-- Persons -->
          <v-tab-item>
            <v-card>
              <v-card-text>
                <v-data-table id="data" :headers="personHeaders" :items="personStats" hide-actions class="elevation-1">
                  <template slot="items" slot-scope="props">
                    <tr :class="{ 'last-in-cell': isLastInCell(props) }">
                      <td v-for="h in personHeaders" :key="h.value" v-html="props.item[h.value]"></td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Cells -->
          <v-tab-item>
            <v-card>
              <v-card-text>
                <v-data-table id="data" :headers="cellHeaders" :items="cellStats" hide-actions class="elevation-1">
                  <template slot="items" slot-scope="props">
                    <td v-for="h in cellHeaders" :key="h.value" v-html="props.item[h.value]"></td>
                  </template>
                  <template slot="footer">
                    <tr>
                      <td v-for="(s, i) in summary" :key="i" v-html="s"></td>
                    </tr>
                  </template>
                </v-data-table>

                <div class="mb-2 mt-2">
                  {{ t('Legend') }}:
                  <table cellspan="6">
                    <tr>
                      <td>{{ t('Number of persons') }}</td>
                      <td>- {{ t('Number of persons visiting during the period') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average frequency') }}</td>
                      <td>- {{ t('Average frequency of members on meetings') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average person count') }}</td>
                      <td>- {{ t('Average person count on a meeting') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Meeting span') }}</td>
                      <td>- {{ t('Meetings frequency in weeks') }}</td>
                    </tr>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Sections -->
          <v-tab-item>
            <v-card>
              <v-card-text>
                <v-data-table
                  id="data"
                  :headers="sectionHeaders"
                  :items="sectionStats"
                  hide-actions
                  class="elevation-1"
                >
                  <template slot="items" slot-scope="props">
                    <td v-for="h in sectionHeaders" :key="h.value" v-html="props.item[h.value]"></td>
                  </template>
                </v-data-table>

                <div class="mb-2 mt-2">
                  {{ t('Legend') }}:
                  <table cellspan="6">
                    <tr>
                      <td>{{ t('Number of persons') }}</td>
                      <td>- {{ t('Number of persons visiting during the period') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average frequency') }}</td>
                      <td>- {{ t('Average frequency of members on meetings') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average person count') }}</td>
                      <td>- {{ t('Average person count on a meeting') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Meeting span') }}</td>
                      <td>- {{ t('Meetings frequency in weeks') }}</td>
                    </tr>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </div>
</template>

<script>
import CellSelect from '@/components/sub/CellSelect'
import Toolbar from '@/components/layout/Toolbar'
import { mapState, mapGetters, mapActions } from 'vuex'
import { mapStateMutations } from '@/store'
import { compareRoleRank } from '@/global'
// import { t } from '@/i18n'

export default {
  components: { CellSelect, Toolbar },

  data() {
    return {
      cellTypeName: 'Open cell',
      periods: ['Month', 'Quarter', 'Half year', 'Year'],
      period: 'Quarter',
      startingPoints: ['Until now', 'Current', 'Previous'],
      startingPoint: 'Until now',
      executed: false,
      active: 0,
      reportCells: [],
    }
  },

  async created() {
    await this.$store.dispatch('authorize')
  },

  // mounted() {
  //   this.$nextTick(() => {
  //     const r = document.querySelector('.v-expansion-panel__header').getBoundingClientRect()
  //     console.log(r)
  //     document.querySelector('.v-expansion-panel__body').styles.height = `calc(100vh - ${r.bottom}px)`
  //   })
  // },

  computed: {
    ...mapState('cell', ['item', 'type', 'names']),
    ...mapState('stats2', ['personStats', 'cellStats', 'sectionStats', 'summary', 'total']),
    ...mapGetters('stats2', ['personHeaders', 'cellHeaders', 'sectionHeaders', 'types', 'role']),
    activeTab: mapStateMutations('stats2/activeTab'),
    from: mapStateMutations('stats2/from'),
    to: mapStateMutations('stats2/to'),
    selectedCell() {
      return this.$store.getters['cell/selected']
    },
    reportAvailableCells() {
      return (this.$store.state.auth.userCells || [])
        .map(name => name && name.trim())
        .filter(name => name)
    },
    reportCellItems() {
      return this.reportAvailableCells.map(name => ({
        text: this.$store.getters['cell/cellDesc'](name),
        value: name,
      }))
    },
    showReportMultiSelect() {
      return this.reportAvailableCells.length > 1
    },
    runDisabled() {
      return this.showReportMultiSelect && !this.reportCells.length
    },
    showSections() {
      console.log(this.$store.getters['stats2/role'], compareRoleRank(this.$store.getters['stats2/role'], 'L'))
      return compareRoleRank(this.$store.getters['stats2/role'], 'L') > 0
    },
  },

  watch: {
    reportAvailableCells: {
      immediate: true,
      handler(cells) {
        if (!cells.length) return
        const selected = this.reportCells.filter(name => cells.includes(name))
        this.reportCells = selected.length ? selected : cells.slice()
      },
    },
    reportCells() {
      if (this.executed && this.showReportMultiSelect && !this.runDisabled) this.run()
    },
    selectedCell() {
      if (this.executed && !this.showReportMultiSelect) this.run()
    },
  },

  methods: {
    ...mapActions('stats2', ['copy', 'find']),

    isLastInCell(props) {
      const a = this.personStats
      const i = props.index
      // console.log(i, a[i + 1].cell, props.item.cell)
      return i < a.length - 1 && a[i + 1].cell !== props.item.cell
    },
    isReportCellSelected(cell) {
      return this.reportCells.includes(cell)
    },
    setReportCell(cell, selected) {
      this.reportCells = selected ?
        this.reportCells.concat(cell).filter((name, i, names) => names.indexOf(name) === i) :
        this.reportCells.filter(name => name !== cell)
    },
    run() {
      if (this.runDisabled) return
      this.find({
        cell: this.showReportMultiSelect ? this.reportCells : this.selectedCell,
        cellTypeName: this.cellTypeName,
        period: this.period,
        startingPoint: this.startingPoint,
      })
      this.$store.commit('maxWidth', '100%')
      this.$nextTick(() => {
        this.executed = true
        this.active = 1
        this.$nextTick(() => this.$store.commit('stats2/activeTab', 0))
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('maxWidth')
    next()
  },
}
</script>

<style lang="stylus">
#cell-type {
  display: flex;
}

#report-cell-select {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding-left: 24px;
  padding-right: 16px;
  padding-top: 0px;
  padding-bottom: 0px;

  .report-cell-select-label {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    height: 40px;
    margin-right: 14px;
    line-height: 40px;
  }

  .report-cell-checkbox {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    height: 40px;
    margin: 0px 24px 0px 0px !important;
    padding: 0px !important;

    .v-input__control {
      height: 40px;
      min-height: 40px;
    }

    .v-input__slot {
      align-items: center;
      height: 40px;
      min-height: 40px;
      margin-bottom: 0px;
    }

    .v-input--selection-controls__input {
      align-self: center;
      margin-right: 10px;
    }

    .v-label {
      display: flex;
      align-items: center;
      top: 0px;
      height: 40px;
      line-height: normal;
    }
  }
}

.v-input__slot {
  margin-bottom: 0px !important;
}

.last-in-cell {
  border-bottom: 1px solid #808080 !important;
}

.v-table__overflow {
  width: auto !important;
}

table.v-datatable.v-table {
  width: auto !important;
}

#cell-type > div {
  vertical-align: middle;
  margin: 0px;
  padding: 0px 8px;
  flex: none;
}

#data {
  border-top: 1px solid rgba(128, 128, 128, 0.25);

  tr {
    height: 32px;
  }

  th, td {
    padding: 0px 4px;
    height: 32px;
    max-height: 32px;
    min-width: 70px;
    white-space: nowrap;
    text-align: center;
    border-right: 1px solid grey;
    border-right-color: rgba(128, 128, 128, 0.12);
  }

  td:first-child {
    text-align: left;
  }

  tfoot {
    background-color, tr:first-child {
      border-top: 1px solid grey;
    }

    td {
      color: rgba(0, 0, 0, 0.54);
      font-size: 12px;
      font-weight: 500;
    }
  }
}
</style>
